import { useCallback, useState } from 'react';

import { useNavigation } from '@react-navigation/native';

import { EntryInfoFragment } from '@/api/entries/query.generated';
import { useAgeCohortValidation } from '@/hooks/use-age-cohort-validation';
import { isUserBelowAgeLimitToPlaySelector, useAuthUserConfig } from '@/hooks/use-auth-user-config';
import { useEntries } from '@/hooks/use-entries';
import { navigateHome } from '@/navigation/navigation';
import { logger } from '@/utils/logging';

import { BetslipPick } from '../types';

export type PlaceEntryStatus = 'idle' | 'loading' | 'success' | 'error';

type UsePlaceEntryOptions = {
    picks: BetslipPick[];
    onSuccess: (entry: EntryInfoFragment) => void | Promise<void>;
    onError?: (error: unknown) => void;
};

export const usePlaceEntry = ({ picks, onSuccess, onError }: UsePlaceEntryOptions) => {
    const { addEntry } = useEntries();
    const [status, setStatus] = useState<PlaceEntryStatus>('idle');
    const { showAgeRestrictionNotice } = useAgeCohortValidation();
    const { data: isBelowAgeLimitToPlay } = useAuthUserConfig({ select: isUserBelowAgeLimitToPlaySelector });
    const navigation = useNavigation();

    const placeEntry = useCallback(
        async (amount: number) => {
            if (isBelowAgeLimitToPlay) {
                showAgeRestrictionNotice();
                setTimeout(() => navigateHome(navigation), 1000);
                return;
            }
            setStatus('loading');
            let entry: EntryInfoFragment;
            try {
                entry = await addEntry({ amount: amount.toString(), picks });
            } catch (e: unknown) {
                setStatus('error');
                onError?.(e);
                return;
            }
            // Entry is placed at this point — post-placement handling must not flip us to error.
            setStatus('success');
            try {
                await onSuccess(entry);
            } catch (e: unknown) {
                logger.error('[PlaceEntry]', 'onSuccess handler failed after entry was placed', e);
            }
        },
        [isBelowAgeLimitToPlay, showAgeRestrictionNotice, navigation, addEntry, picks, onSuccess, onError]
    );

    return { placeEntry, status, isLoading: status === 'loading' };
};
